import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Banknote,
  Building2,
  CalendarCheck,
  CreditCard,
  Link2,
  Loader2,
  Mail,
  Smartphone,
  Ticket,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { ManualPaymentFields } from '@/components/invoices/ManualPaymentFields';
import { MobilePayRequestConfirm } from '@/components/payments/MobilePayRequestConfirm';
import { useClinic } from '@/hooks/useClinicSettings';
import { deriveClinicPayDisplayStatus, useClinicPayStatusLocal } from '@/hooks/useClinicPay';
import { usePatientActivePunchCards } from '@/hooks/usePunchCardPayment';
import {
  StaffPaymentChoiceError,
  useStaffAppointmentPayment,
} from '@/hooks/useStaffAppointmentPayment';
import { buildAppointmentInvoiceHref } from '@/hooks/useAppointmentInvoicing';
import { TYPE, UI_MUTED } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { assertNever, parseBookingPaymentMethods, paymentMethodIsEnabled } from '@/lib/bookingPolicies';
import {
  hasValidPatientEmail,
  isClinioPayAvailableForStaff,
  pickPreferredPunchCard,
  resolveStaffPaymentChoices,
  selectApplicablePunchCards,
  totalApplicableClips,
  type StaffPaymentChoice,
} from '@/lib/calendarPaymentChoice';
import { uiDateLocale } from '@/lib/i18n';
import {
  isPaymentDateOnOrBeforeToday,
  isPaymentRequestMethod,
  paymentDateInputToIso,
  recordedPaymentMethod,
  storedPaymentMethodLabel,
  todayDateInputValue,
} from '@/lib/manualPaymentMethods';
import {
  mobilePayRequestBlock,
  mobilePayRequestErrorKey,
} from '@/lib/mobilePayRequest';
import { appointmentSaveErrorI18nKey } from '@/utils/appointmentSaveError';

export interface AppointmentPaymentChoicePatient {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

export interface AppointmentPaymentChoiceProps {
  clinicId: string;
  appointmentId?: string | null;
  patient: AppointmentPaymentChoicePatient;
  serviceId: string | null;
  serviceName: string | null;
  startsAt: string;
  priceCents: number;
  currency: string;
  onFinished: () => void;
  onCancel?: () => void;
  onEnsureAppointment?: () => Promise<{ id: string }>;
  onBookingConfirmed?: (appointmentId: string) => void;
  allowConfirmWithoutPayment?: boolean;
  onConfirmWithoutPayment?: () => Promise<void>;
  onBusyChange?: (busy: boolean) => void;
  embedded?: boolean;
  canRedeemPunchCard?: boolean;
  canSettleInvoice?: boolean;
  /** An open invoice is already the settlement. Klippekort replacement uses the change-payment path. */
  blockPunchCard?: boolean;
  onOpenInvoice?: (invoiceId: string) => void;
}

type ChoiceResult =
  | { kind: 'punch_card'; clipsUsed: number }
  | { kind: 'recorded'; invoiceId: string; method: string };

function formatMoney(cents: number, currency: string, locale: string): string {
  return (cents / 100).toLocaleString(locale, { style: 'currency', currency });
}

export function AppointmentPaymentChoice(props: AppointmentPaymentChoiceProps) {
  const { t } = useTranslation();
  const {
    clinicId,
    appointmentId,
    patient,
    serviceId,
    serviceName,
    startsAt,
    priceCents,
    currency,
    onFinished,
    onCancel,
    onEnsureAppointment,
    onBookingConfirmed,
    allowConfirmWithoutPayment = false,
    onConfirmWithoutPayment,
    onBusyChange,
    embedded = false,
    canRedeemPunchCard = true,
    canSettleInvoice = true,
    blockPunchCard = false,
    onOpenInvoice,
  } = props;

  const navigate = useNavigate();

  const { data: clinic } = useClinic(clinicId);
  const { data: payStatus, isFetched: payStatusFetched } = useClinicPayStatusLocal(clinicId);
  const { data: activePunchCards, isFetched: punchCardsFetched } = usePatientActivePunchCards(
    patient.id,
    clinicId,
  );
  const payment = useStaffAppointmentPayment();

  const [result, setResult] = useState<ChoiceResult | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [invoiceStarted, setInvoiceStarted] = useState(false);
  const [collectedCents, setCollectedCents] = useState(0);
  const [selectedPunchCardId, setSelectedPunchCardId] = useState<string>('');
  const [resolvedAppointmentId, setResolvedAppointmentId] = useState<string | null>(
    appointmentId ?? null,
  );
  const [confirmingWithoutPayment, setConfirmingWithoutPayment] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualMethod, setManualMethod] = useState('');
  const [manualOther, setManualOther] = useState('');
  const [manualAmount, setManualAmount] = useState('');
  const [manualDate, setManualDate] = useState(() => todayDateInputValue());
  const [mobilePayConfirm, setMobilePayConfirm] = useState(false);

  const methods = parseBookingPaymentMethods(clinic?.booking_payment_methods);
  const applicableCards = useMemo(
    () => selectApplicablePunchCards(activePunchCards ?? [], serviceId),
    [activePunchCards, serviceId],
  );
  const preferredCard = pickPreferredPunchCard(applicableCards);
  const selectedCard =
    applicableCards.find((card) => card.id === selectedPunchCardId) ?? preferredCard;
  const clipsAvailable = totalApplicableClips(applicableCards);
  const mobilePayAvailable = isClinioPayAvailableForStaff({
    useClinioPay: clinic?.use_clinio_pay,
    payDisplayStatus: deriveClinicPayDisplayStatus(payStatus),
  });
  const outstandingCents = Math.max(0, priceCents - collectedCents);
  const choices = resolveStaffPaymentChoices({
    methods,
    applicablePunchCards: applicableCards,
    mobilePayAvailable,
    chargeAmountCents: outstandingCents,
    canRedeemPunchCard,
    canSettleInvoice,
  }).filter(
    (choice) =>
      !(choice === 'punch_card' && (blockPunchCard || invoiceStarted)),
  );
  const optionsReady = Boolean(clinic) && payStatusFetched && punchCardsFetched;
  const busy = payment.isPending || confirmingWithoutPayment;
  const activeAppointmentId = appointmentId ?? resolvedAppointmentId;

  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  async function ensureAppointmentId(): Promise<string> {
    if (activeAppointmentId) return activeAppointmentId;
    if (!onEnsureAppointment) {
      throw new Error('Missing appointment');
    }
    const created = await onEnsureAppointment();
    setResolvedAppointmentId(created.id);
    return created.id;
  }

  async function handleConfirmWithoutPayment() {
    if (!onConfirmWithoutPayment) return;
    setErrorCode(null);
    setConfirmingWithoutPayment(true);
    try {
      await onConfirmWithoutPayment();
    } catch (error) {
      if (!(error instanceof StaffPaymentChoiceError)) {
        toast.error(t(appointmentSaveErrorI18nKey(error, false)));
      } else {
        setErrorCode(error.code);
        toast.error(t('appointments.errors.unknown'));
      }
    } finally {
      setConfirmingWithoutPayment(false);
    }
  }

  const locale = uiDateLocale();
  const startDate = new Date(startsAt);
  const patientName = `${patient.first_name ?? ''} ${patient.last_name ?? ''}`.trim() || patient.id;
  const dateLabel = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(
    startDate,
  );
  const timeLabel = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(startDate);
  const moneyCurrency = currency || clinic?.default_currency || 'DKK';
  const amountLabel = formatMoney(priceCents, moneyCurrency, locale);
  const outstandingLabel = formatMoney(outstandingCents, moneyCurrency, locale);

  async function handleChoice(choice: StaffPaymentChoice) {
    setErrorCode(null);
    try {
      if (choice === 'punch_card') {
        const cardId = selectedCard?.id ?? preferredCard?.id;
        if (!cardId) {
          setErrorCode('klip_failed');
          return;
        }
      }
      if (choice === 'invoice_email' && !hasValidPatientEmail(patient.email)) {
        setErrorCode('missing_email');
        toast.error(t('appointments.payment_choice.errors.missing_email'));
        return;
      }

      const bookingId = await ensureAppointmentId();
      switch (choice) {
        case 'punch_card': {
          const cardId = selectedCard?.id ?? preferredCard?.id;
          if (!cardId) {
            setErrorCode('klip_failed');
            return;
          }
          await payment.redeemKlip.mutateAsync({
            punchCardId: cardId,
            appointmentId: bookingId,
            patientId: patient.id,
            clinicId,
            clipsToUse: 1,
          });
          setResult({ kind: 'punch_card', clipsUsed: 1 });
          onBookingConfirmed?.(bookingId);
          toast.success(t('appointments.payment_choice.klip_used', { count: 1 }));
          return;
        }
        case 'invoice_email': {
          setInvoiceStarted(true);
          await payment.sendInvoiceByEmail.mutateAsync({
            clinicId,
            patientId: patient.id,
            appointmentId: bookingId,
            patientEmail: patient.email,
          });
          onBookingConfirmed?.(bookingId);
          toast.success(t('appointments.payment_choice.invoice_sent'));
          return;
        }
        case 'payment_link':
        case 'mobilepay':
        case 'card': {
          if (choice === 'mobilepay') {
            const block = mobilePayRequestBlock({
              ready: optionsReady,
              mobilePayEnabled: paymentMethodIsEnabled(methods, 'mobilepay'),
              clinioPayAvailable: mobilePayAvailable,
              outstandingCents,
            });
            if (block) {
              setErrorCode(block);
              toast.error(t(mobilePayRequestErrorKey(block)));
              return;
            }
          }
          setInvoiceStarted(true);
          await payment.sendClinioPay.mutateAsync({
            clinicId,
            patientId: patient.id,
            appointmentId: bookingId,
            paymentMethod: choice,
          });
          setMobilePayConfirm(false);
          onBookingConfirmed?.(bookingId);
          toast.success(
            choice === 'card'
              ? t('appointments.payment_choice.card_sent')
              : choice === 'mobilepay'
                ? t('appointments.payment_choice.mobilepay_sent')
                : t('appointments.payment_choice.payment_link_sent'),
          );
          return;
        }
        case 'cash':
        case 'bank': {
          setInvoiceStarted(true);
          const recordedMethod = recordedPaymentMethod(manualMethod, manualOther);
          if (!recordedMethod) {
            setErrorCode('desk_payment_failed');
            toast.error(t('invoices.payment_method_required', 'Select a payment method'));
            return;
          }
          const amountCents = Math.round(Number(manualAmount.replace(',', '.')) * 100);
          if (!Number.isFinite(amountCents) || amountCents <= 0) {
            setErrorCode('desk_payment_failed');
            toast.error(t('invoices.payment_invalid_amount', 'Invalid payment amount'));
            return;
          }
          if (!isPaymentDateOnOrBeforeToday(manualDate)) {
            setErrorCode('desk_payment_failed');
            toast.error(t('invoices.payment_date_invalid', 'Choose a payment date that is not in the future'));
            return;
          }
          const recorded = await payment.recordDeskPayment.mutateAsync({
            clinicId,
            patientId: patient.id,
            appointmentId: bookingId,
            paymentMethod: recordedMethod,
            amountCents,
            paymentDate: paymentDateInputToIso(manualDate),
          });
          const nextCollected = collectedCents + amountCents;
          onBookingConfirmed?.(bookingId);
          toast.success(
            recorded.prepaid
              ? t('appointments.payment_choice.prepaid_saved', 'Forudbetaling gemt. Fakturaen kommer, når besøget afsluttes.')
              : t('invoices.payment_recorded', 'Payment recorded successfully'),
          );
          if (recorded.prepaid) {
            setResult({ kind: 'recorded', invoiceId: '', method: recordedMethod });
            return;
          }
          if (priceCents > 0 && nextCollected >= priceCents && recorded.invoice) {
            setResult({ kind: 'recorded', invoiceId: recorded.invoice.id, method: recordedMethod });
          } else {
            setCollectedCents(nextCollected);
            setManualAmount(((priceCents - nextCollected) / 100).toFixed(2));
          }
          return;
        }
        default:
          return assertNever(choice);
      }
    } catch (error) {
      if (!(error instanceof StaffPaymentChoiceError)) {
        toast.error(t(appointmentSaveErrorI18nKey(error, false)));
        return;
      }
      const code = error.code;
      setErrorCode(code);
      switch (code) {
        case 'missing_email':
          toast.error(t('appointments.payment_choice.errors.missing_email'));
          break;
        case 'invoice_create_failed':
        case 'invoice_issue_failed':
          toast.error(t('appointments.payment_choice.errors.invoice_failed'));
          break;
        case 'invoice_email_failed':
          toast.error(t('appointments.payment_choice.errors.email_failed'));
          break;
        case 'mobilepay_failed':
          toast.error(t('appointments.payment_choice.errors.mobilepay_failed'));
          break;
        case 'card_failed':
          toast.error(t('appointments.payment_choice.errors.card_failed'));
          break;
        case 'payment_link_failed':
          toast.error(t('appointments.payment_choice.errors.payment_link_failed'));
          break;
        case 'desk_payment_failed':
          toast.error(t('appointments.payment_choice.errors.desk_failed'));
          break;
        case 'already_paid':
        case 'already_settled':
          toast.error(t('appointments.payment.already_paid'));
          break;
        case 'invoice_already_issued':
          toast.error(t('appointments.payment_choice.errors.invoice_already_issued'));
          break;
        case 'invoice_paid_locked':
          toast.error(t('appointments.details.change_payment_paid_locked'));
          break;
        case 'klip_failed':
          toast.error(t('appointments.payment.punch_card_payment_failed'));
          break;
        default:
          toast.error(t('appointments.errors.unknown'));
      }
    }
  }

  if (result) {
    const invoiceId = 'invoiceId' in result ? result.invoiceId : null;
    return (
      <div className={embedded ? 'space-y-4' : 'space-y-4 p-6'}>
        <ResultView result={result} />
        <div className="flex justify-end gap-2">
          {invoiceId ? (
            <Button
              type="button"
              data-testid="open-invoice"
              onClick={() => {
                if (onOpenInvoice) {
                  onOpenInvoice(invoiceId);
                  return;
                }
                navigate(
                  buildAppointmentInvoiceHref({
                    invoiceId,
                    patientId: patient.id,
                    clinicId,
                    appointmentId: activeAppointmentId ?? appointmentId ?? '',
                    returnTo: `/patients/${patient.id}`,
                  }),
                );
                onFinished();
              }}
            >
              {t('appointments.open_invoice')}
            </Button>
          ) : null}
          <Button type="button" variant="secondary" onClick={onFinished}>
            {t('common.close')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? 'space-y-3' : 'space-y-4 p-6'}>
      {embedded ? (
        <h5 className={cn(TYPE.card, 'text-ds-text')}>
          {t('appointments.payment_choice.select_method')}
        </h5>
      ) : (
        <div>
          <h4 className="text-base font-medium text-ds-text">
            {t('appointments.payment_choice.title')}
          </h4>
          <p className={cn('mt-2', TYPE.body, 'text-ds-text')}>{patientName}</p>
          <p className={UI_MUTED}>{serviceName ?? t('appointments.modal.service_none')}</p>
          <p className={UI_MUTED}>
            {dateLabel} · {timeLabel}
          </p>
          {priceCents > 0 && (
            <p className="mt-1 text-sm font-medium text-ds-text">{amountLabel}</p>
          )}
        </div>
      )}

      {errorCode === 'missing_email' && (
        <p className="rounded-md bg-ds-amber-tint px-3 py-2 text-sm text-ds-amber-ink">
          {t('appointments.payment_choice.errors.missing_email')}
        </p>
      )}
      {errorCode === 'invoice_email_failed' && (
        <p className="rounded-md bg-ds-amber-tint px-3 py-2 text-sm text-ds-amber-ink">
          {t('appointments.payment_choice.errors.email_failed')}
        </p>
      )}

      {mobilePayConfirm && (
        <MobilePayRequestConfirm
          amountLabel={outstandingLabel}
          patientName={patientName}
          block={mobilePayRequestBlock({
            ready: optionsReady,
            mobilePayEnabled: paymentMethodIsEnabled(methods, 'mobilepay'),
            clinioPayAvailable: mobilePayAvailable,
            outstandingCents,
          })}
          ready={optionsReady}
          busy={busy}
          onCancel={() => {
            setMobilePayConfirm(false);
            if (isPaymentRequestMethod(manualMethod)) setManualMethod('');
          }}
          onConfirm={() => {
            void handleChoice('mobilepay');
          }}
        />
      )}

      {!optionsReady ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-ds-muted" aria-hidden="true" />
        </div>
      ) : (
        <div className="space-y-4" data-payment-dialog="">
          {choices.includes('punch_card') && (
            <section className="space-y-2" data-settlement-type="punch_card">
              <h6 className="text-sm font-medium text-ds-text">
                {t('appointments.payment_dialog.punch_heading', 'Klippekort')}
              </h6>
              <p className={UI_MUTED}>
                {t(
                  'appointments.payment_dialog.punch_help',
                  'Brug ét klip. Der oprettes ingen faktura.',
                )}
              </p>
              {applicableCards.length > 1 && (
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-ds-muted">
                    {t('appointments.payment.select_punch_card')}
                  </span>
                  <select
                    value={selectedCard?.id ?? ''}
                    onChange={(e) => setSelectedPunchCardId(e.target.value)}
                    className="ds-field"
                  >
                    {applicableCards.map((card) => (
                      <option key={card.id} value={card.id}>
                        {card.name} — {card.remaining_clips}{' '}
                        {t('appointments.payment.clips_remaining')}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <ChoiceButton
                choice="punch_card"
                clipsAvailable={selectedCard?.remaining_clips ?? clipsAvailable}
                disabled={busy}
                onSelect={handleChoice}
              />
            </section>
          )}
          {choices.some((choice) => choice !== 'punch_card') && (
            <section className="space-y-2" data-settlement-type="invoice_payment">
              <h6 className="text-sm font-medium text-ds-text">
                {t('appointments.payment_dialog.money_heading', 'Faktura / betaling')}
              </h6>
              <p className={UI_MUTED}>
                {t('appointments.payment_dialog.money_help', 'Betalingsmetode')}
              </p>
              {choices
                .filter((choice) => choice !== 'punch_card')
                .map((choice) => (
                  <ChoiceButton
                    key={choice}
                    choice={choice}
                    clipsAvailable={clipsAvailable}
                    disabled={busy}
                    onSelect={(next) => {
                      if (next === 'mobilepay') {
                        setMobilePayConfirm(true);
                        return;
                      }
                      if (next === 'cash' || next === 'bank') {
                        setManualOpen(true);
                        setManualMethod(next === 'bank' ? 'bank_transfer' : 'cash');
                        setManualOther('');
                        setManualAmount((outstandingCents / 100).toFixed(2));
                        setManualDate(todayDateInputValue());
                        return;
                      }
                      void handleChoice(next);
                    }}
                  />
                ))}
              {manualOpen && (
                <form
                  className="space-y-2 rounded-lg border border-ds-line p-3"
                  data-manual-payment=""
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (isPaymentRequestMethod(manualMethod)) {
                      setMobilePayConfirm(true);
                      return;
                    }
                    void handleChoice(manualMethod === 'bank_transfer' ? 'bank' : 'cash');
                  }}
                >
                  <label className="block text-xs font-medium text-ds-muted">
                    {t('invoices.payment_amount', 'Beløb')}
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      required
                      value={manualAmount}
                      onChange={(event) => setManualAmount(event.target.value)}
                      className="ds-field mt-1"
                    />
                  </label>
                  <ManualPaymentFields
                    idPrefix="appointment-payment"
                    method={manualMethod}
                    onMethodChange={(next) => {
                      setManualMethod(next);
                      if (isPaymentRequestMethod(next)) setMobilePayConfirm(true);
                    }}
                    otherDetail={manualOther}
                    onOtherDetailChange={setManualOther}
                    paymentDate={manualDate}
                    onPaymentDateChange={setManualDate}
                  />
                  <Button type="submit" disabled={busy}>
                    {isPaymentRequestMethod(manualMethod)
                      ? t('invoices.mobilepay_request_send')
                      : t('invoices.record_payment', 'Registrer betaling')}
                  </Button>
                </form>
              )}
            </section>
          )}
          {allowConfirmWithoutPayment && onConfirmWithoutPayment && (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                void handleConfirmWithoutPayment();
              }}
              aria-label={t('appointments.payment_choice.confirm_without_payment')}
              className="flex w-full items-center gap-3 rounded-lg border border-ds-line bg-ds-card px-3 py-2 text-left text-sm hover:bg-ds-row-hover disabled:cursor-not-allowed disabled:opacity-60"
            >
              <CalendarCheck
                className="h-4 w-4 shrink-0 text-ds-teal"
                aria-hidden="true"
              />
              <span className="block font-medium text-ds-text">
                {t('appointments.payment_choice.confirm_without_payment')}
              </span>
            </button>
          )}
          {choices.length === 0 && !allowConfirmWithoutPayment && (
            <p className="text-sm text-ds-muted">
              {t('appointments.payment.no_methods', 'Ingen betalingsmetoder tilgængelige')}
            </p>
          )}
        </div>
      )}

      <div className="flex justify-end border-t border-ds-line pt-3">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel ?? onFinished}
          disabled={busy}
        >
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  );
}

function ChoiceButton({
  choice,
  clipsAvailable,
  disabled,
  onSelect,
}: {
  choice: StaffPaymentChoice;
  clipsAvailable: number;
  disabled: boolean;
  onSelect: (choice: StaffPaymentChoice) => void;
}) {
  const { t } = useTranslation();
  let Icon = Ticket;
  let labelKey = 'appointments.payment_choice.use_clip';
  switch (choice) {
    case 'punch_card':
      Icon = Ticket;
      labelKey = 'appointments.payment.use_clip';
      break;
    case 'card':
      Icon = CreditCard;
      labelKey = 'appointments.payment_choice.send_card';
      break;
    case 'mobilepay':
      Icon = Smartphone;
      labelKey = 'appointments.payment_choice.send_mobilepay';
      break;
    case 'payment_link':
      Icon = Link2;
      labelKey = 'appointments.payment_choice.send_payment_link';
      break;
    case 'cash':
      Icon = Banknote;
      labelKey = 'appointments.payment_choice.register_cash';
      break;
    case 'bank':
      Icon = Building2;
      labelKey = 'appointments.payment_choice.register_bank';
      break;
    case 'invoice_email':
      Icon = Mail;
      labelKey = 'appointments.payment_choice.send_invoice_email';
      break;
    default:
      assertNever(choice);
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onSelect(choice)}
      aria-label={t(labelKey)}
      className="flex w-full items-center gap-3 rounded-lg border border-ds-line bg-ds-card px-3 py-2 text-left text-sm hover:bg-ds-row-hover disabled:cursor-not-allowed disabled:opacity-60"
    >
      <Icon className="h-4 w-4 shrink-0 text-ds-teal" aria-hidden="true" />
      <span>
        <span className="block font-medium text-ds-text">{t(labelKey)}</span>
        {choice === 'punch_card' && (
          <span className="mt-0.5 block text-xs text-ds-muted">
            {t('appointments.payment_choice.clips_remaining_count', { count: clipsAvailable })}
          </span>
        )}
      </span>
    </button>
  );
}

function ResultView({ result }: { result: ChoiceResult }) {
  const { t } = useTranslation();

  switch (result.kind) {
    case 'punch_card':
      return (
        <div>
          <p className="text-sm font-medium text-ds-text">
            {t('appointments.payment_choice.klip_used', { count: result.clipsUsed })}
          </p>
          <p className="mt-1 text-sm text-ds-muted">
            {t('appointments.payment.punch_card_payment_success')}
          </p>
        </div>
      );
    case 'recorded':
      return (
        <p className="text-sm font-medium text-ds-text">
          {t('invoices.payment_recorded', 'Payment recorded successfully')} ·{' '}
          {storedPaymentMethodLabel(result.method, t)}
        </p>
      );
    default:
      return assertNever(result);
  }
}
